import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Mail, User, Calendar, Shield, Briefcase } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/lib/customSupabaseClient';
import { AlertCircle } from 'lucide-react';
import { SendEmailButton } from '@/components/email/SendEmailButton';

function TableSkeleton({ rows = 6 }) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead><Skeleton className="h-4 w-20" /></TableHead>
            <TableHead><Skeleton className="h-4 w-20" /></TableHead>
            <TableHead><Skeleton className="h-4 w-24" /></TableHead>
            <TableHead className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-40" />
              </TableCell>
              <TableCell><Skeleton className="h-4 w-28" /></TableCell>
              <TableCell><Skeleton className="h-5 w-20" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-24 ml-auto" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const B2B = () => {
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivations = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('activation_dashboard')
          .select('user_name, user_email, program_name, activated_at, organization')
          .order('activated_at', { ascending: false });

        if (error) throw error;
        setActivations(data);
      } catch (err) {
        console.error("Error fetching activation data:", err);
        setError('Failed to load activation data. Please try again.');
        toast.error("Could not fetch B2B activation data.");
      } finally {
        setLoading(false);
      }
    };
    fetchActivations();
  }, []);

  return (
    <>
      <Helmet>
        <title>B2B Dashboard - My Movement Space</title>
      </Helmet>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">B2B Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Activation code management and partner activity.
          </p>
        </div>

        <Card className="shadow-none">
          <CardHeader>
            <CardTitle>Recent Partner Activations</CardTitle>
            <CardDescription>
              Live feed of the latest program activations by B2B partners.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <TableSkeleton />
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-destructive">
                <AlertCircle className="h-6 w-6 mb-2" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead><User className="inline-block h-4 w-4 mr-1.5" />User</TableHead>
                      <TableHead><Briefcase className="inline-block h-4 w-4 mr-1.5" />Program</TableHead>
                      <TableHead><Shield className="inline-block h-4 w-4 mr-1.5" />Organization</TableHead>
                      <TableHead className="text-right"><Calendar className="inline-block h-4 w-4 mr-1.5" />Activated At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activations.length > 0 ? (
                      activations.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <div className="font-medium">{item.user_name}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {item.user_email}
                              <SendEmailButton userId={item.user_id} fullName={item.user_name} email={item.user_email} />
                            </div>
                          </TableCell>
                          <TableCell>{item.program_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.organization}</Badge>
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {new Date(item.activated_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                          No recent activations found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default B2B;
